import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getInventoryRun, getAllInventoryRunItems, getInventoryDivergences, getInventoryAisleCoverage,
  reviewDivergences, unreviewDivergences, type Disposition,
  listStorageLocations, listInventoryMaps, updateInventoryRunMap, renameInventoryRun,
  deleteInventoryRun,
  appendInventoryBundle,
  getInventoryChanges, getInventoryPasses, getInventoryProducts, getInventoryStockReport, DuplicateAddressesError,
  type InventoryRunSummary, type DivergenceRow, type InventoryScanRow, type InventoryReject,
  type DuplicatePolicy, type InventoryChangeGeneration, type InventoryPass,
  type InventoryPassComparison, type InventoryUnidentifiedStock,
  type InventoryOccupancyMeasurement,
  type InventoryReconRow, type ReconSituation,
  listExpectedStockSnapshots, bindInventoryRunSnapshots,
} from "../api/rest";
import { divergencesToCsv, aisleReconciliation, addressPalletsToCsv, downloadTextFile, downloadBinaryFile, type AisleRecon } from "./inventoryReport";
import { divergencesToXlsx } from "./divergenceSheet";
import { flexReportToXlsx } from "./flexReport";
import { ageBuckets, oldestStock, hasAging } from "./inventoryAging";
import { planRackImages } from "./rackLayout";
import { summarizeLevels, situationTone, tallyTones } from "./inventoryCounts";
import { rejectReasonLabel } from "./rejectReasons";
import { resolveSku } from "./skuResolution";
import { useSiteId } from "../hooks/useSiteId";
import { useTableSort, type SortAccessor } from "../app/table/useTableSort";
import { SortableTh } from "../app/table/SortableTh";
import { StackedBarChart, type ChartRow } from "../app/charts/StackedBarChart";
import { CollapsibleSection } from "../app/layout/CollapsibleSection";
// Shared page/table shell (.page, .h1, .sub, .tableScroll, .table, .mono,
// .empty, .error, .pill*) is reused from FleetPage.module.css, same as
// InventoryPage.tsx — see that file for the convention.
import styles from "./FleetPage.module.css";
import run from "./InventoryRunPage.module.css";
import { StructureOccupancySection } from "./StructureOccupancySection";
import { SlottingInsightsSection } from "./SlottingInsightsSection";
import { RunTrailSection } from "./RunTrailSection";
import { useSiteStore, ROLE_RANK } from "../stores/siteStore";
import { CoverageGridSection } from "./CoverageGridSection";

const STATUS_PILL: Record<InventoryRunSummary["status"], string> = {
  imported: styles.pill_loaded,
  partial: styles.pill_idle,
  failed: styles.pill_linkdown,
};

// Reuses the same generic 4-color pill set as STATUS_PILL above (green/amber/
// red/blue) — not forklift-status-specific, see that mapping for precedent.
// ADR 364: the review pill names the disposition, not just "justified".
const DISPOSITION_LABEL: Record<string, string> = {
  accepted: "inventory.review.justified",
  recount: "inventory.review.disposition.recount",
  adjust_wms: "inventory.review.disposition.adjust_wms",
};
const DIV_STATUS_PILL: Record<DivergenceRow["status"], string> = {
  match: styles.pill_loaded,
  qty_mismatch: styles.pill_idle,
  missing_in_ic3: styles.pill_linkdown,
  unexpected_in_ic3: styles.pill_empty,
  // ADR 384: explained by the closing snapshot — the calm pill, nothing to act on.
  received_in_window: styles.pill_empty,
  shipped_in_window: styles.pill_empty,
};
const DISPOSITION_PILL: Record<string, string> = {
  accepted: run.reviewPill,
  recount: run.recountPill,
  adjust_wms: run.adjustPill,
};

// Same copy doubles as the divergence-class KPI tile label and the per-row
// Situação text — a row IS an instance of its class.
const DIV_STATUS_LABEL: Record<DivergenceRow["status"], string> = {
  match: "inventory.wms.matched",
  qty_mismatch: "inventory.wms.qtyMismatch",
  missing_in_ic3: "inventory.wms.missing",
  unexpected_in_ic3: "inventory.wms.unexpected",
  received_in_window: "inventory.wms.receivedInWindow",
  shipped_in_window: "inventory.wms.shippedInWindow",
};

// Header sorting (ADR 151): one accessor map per table, module-level so each
// sort memo only recomputes on data/direction changes. Dates compare as epoch;
// status columns sort by their raw key, which groups rows by class; the
// "__none__" recon bucket maps to null so out-of-master sinks like other
// value-less cells.
const NO_ROWS: never[] = [];
/** Rows the reconciliation table will render at once (ADR 233). Enough to read a
 *  whole warehouse's divergences without scrolling to a second page; small enough
 *  that the report paints. Anything beyond it is stated on screen, never dropped
 *  quietly, and the CSV export always carries every row. */
const DIV_ROW_CAP = 500;
/** Occupancy bars drawn per level before the rest is offered behind a click
 *  (ADR 233). A level's own count and average are stated above its chart, so
 *  nothing is lost — only the number of bars is bounded. */
const OCC_ROWS_PER_LEVEL = 60;
const REJECT_COLS: Record<"reason" | "line", SortAccessor<InventoryReject>> = {
  reason: (r) => r.reason,
  line: (r) => r.line,
};
const ITEM_COLS: Record<"address" | "item" | "product" | "quantity" | "status" | "scanTs", SortAccessor<InventoryScanRow>> = {
  address: (r) => r.address,
  item: (r) => r.item ?? r.product,
  product: (r) => r.product,
  quantity: (r) => r.quantity,
  status: (r) => r.status,
  scanTs: (r) => (r.scanTs ? Date.parse(r.scanTs) : null),
};
const RECON_COLS: Record<"aisle" | "keys" | "matched" | "qtyMismatch" | "missing" | "unexpected" | "accuracy", SortAccessor<AisleRecon>> = {
  aisle: (a) => (a.aisle === "__none__" ? null : a.aisle),
  keys: (a) => a.keys,
  matched: (a) => a.matched,
  qtyMismatch: (a) => a.qtyMismatch,
  missing: (a) => a.missing,
  unexpected: (a) => a.unexpected,
  accuracy: (a) => a.accuracyPct,
};
const DIV_COLS: Record<"address" | "product" | "expected" | "counted" | "status" | "age", SortAccessor<DivergenceRow>> = {
  address: (r) => r.address,
  product: (r) => r.product,
  expected: (r) => r.expectedQty,
  counted: (r) => r.countedQty,
  status: (r) => r.status,
  age: (r) => r.ageDays,
};

/** One before→after transition of an address between two uploads (ADR 153). */
interface ChangeRowView {
  key: string; address: string; when: string | null;
  before: string; after: string; outcome: "replaced" | "both" | "ignored";
}
const CHANGE_COLS: Record<"address" | "when" | "before" | "after" | "outcome", SortAccessor<ChangeRowView>> = {
  address: (r) => r.address,
  when: (r) => (r.when ? Date.parse(r.when) : null),
  before: (r) => r.before,
  after: (r) => r.after,
  outcome: (r) => r.outcome,
};

const chartTotal = (r: ChartRow) => r.segments.reduce((n, s) => n + s.value, 0);

// ---- two-pass walk report (ADR 154) ----
const NO_PASSES: InventoryPass[] = [];
const NO_COMPARISONS: InventoryPassComparison[] = [];

const PASS_COLS: Record<string, SortAccessor<InventoryPass>> = {
  file: (p) => p.fileName,
  aisle: (p) => p.aisle,
  direction: (p) => p.direction,
  started: (p) => (p.startedAt ? Date.parse(p.startedAt) : null),
  readings: (p) => p.readings,
  labels: (p) => p.labels,
};

const NO_UNIDENTIFIED: InventoryUnidentifiedStock[] = [];
const NO_OCC: InventoryOccupancyMeasurement[] = [];

const UNIDENTIFIED_COLS: Record<string, SortAccessor<InventoryUnidentifiedStock>> = {
  address: (u) => u.address,
  occupancy: (u) => u.occupancyPct,
  height: (u) => u.heightMm,
};

// Two counts, one pass per level (ADR 159).
const NO_RECON: InventoryReconRow[] = [];
const RECON_ROW_COLS: Record<"address" | "count1" | "count2" | "situation", SortAccessor<InventoryReconRow>> = {
  address: (r) => r.address,
  count1: (r) => r.count1,
  count2: (r) => r.count2,
  situation: (r) => r.situation,
};
// agree_empty tints green with agree_label: both are agreement, and "both empty"
// is a real concordant reading, not a miss. only_count1/2 (a bin one count never
// reached) reads as info, distinct from a label the passes actually disagree on.
const SITUATION_PILL: Record<ReconSituation, string> = {
  agree_label:   styles.pill_loaded,
  agree_empty:   styles.pill_loaded,
  one_pass_only: styles.pill_idle,
  conflict:      styles.pill_linkdown,
  only_count1:   styles.pill_empty,
  only_count2:   styles.pill_empty,
  duplicated:    styles.pill_offline,
};

const PASS_CMP_COLS: Record<string, SortAccessor<InventoryPassComparison>> = {
  aisle: (c) => c.aisle,
  compared: (c) => c.addressesCompared,
  agreement: (c) => c.agreementPct,
  onePassOnly: (c) => c.onePassOnly,
  conflict: (c) => c.labelConflict,
  union: (c) => c.unionLabels,
  forwardRecall: (c) => c.forwardRecallPct,
  backRecall: (c) => c.backRecallPct,
  missed: (c) => c.estimatedMissedLabels,
};

/** Divergence percentages arrive already on a 0–100 scale (DivSummary), unlike
 *  the 0–1 ModularOee fractions elsewhere — no ×100 here. */
function fmtPct1(x: number): string {
  return `${x.toFixed(1).replace(".", ",")}%`;
}

/** Millimetres to metres, two decimals, decimal comma (pt-BR): 1916 -> "1,92". */
function fmtM(mm: number): string {
  return (mm / 1000).toFixed(2).replace(".", ",");
}
/** The kit's occupancy can, at the sensor's edges, read past 100; clamp so a
 *  stray value never overruns the slot it is drawn inside. */
function clampPct(x: number): number {
  return Math.max(0, Math.min(100, x));
}

/**
 * Scan timestamps render on the WAREHOUSE's clock, not the viewer's (ADR 265).
 *
 * Since ADR 265 the stored value is a real instant — the parser reads the iC3
 * export's naive wall clock on the site's own zone instead of pinning it to UTC.
 * That makes the viewer's zone a legitimate rendering, and the wrong one: "the
 * count ran 07:00 to 15:30" is a statement about the floor, and a supervisor in
 * another country reading it in their own morning learns nothing true. So the
 * site's zone is passed in, and the page names it once so the reader knows which
 * clock they are on.
 *
 * Only floor times get this. importedAt/createdAt describe an operator's action
 * in the product, not an event in the warehouse, and stay in the viewer's zone.
 */
function scanZone(timeZone: string | undefined): Intl.DateTimeFormatOptions {
  return timeZone ? { timeZone } : {};
}
function fmtScanTime(iso: string, tz: string | undefined): string {
  return new Date(iso).toLocaleTimeString("pt-BR", scanZone(tz));
}
function fmtScanDateTime(iso: string, tz: string | undefined): string {
  return new Date(iso).toLocaleString("pt-BR", scanZone(tz));
}

/** Quantities are plain counts (no thousands separator, matching this page's
 *  existing Kpi/items-table convention); only guard the rare fractional
 *  expectedQty (BigDecimal) for the decimal-comma rule. */
function fmtQty(n: number | null): string {
  return n == null ? "—" : String(n).replace(".", ",");
}

/** Age in days is a plain count like quantities; "—" when the WMS snapshot
 *  carries no FIFO date for the row (ADR 194). */
function fmtAgeDays(n: number | null): string {
  return n == null ? "—" : `${n} d`;
}

/** FIFO dates are calendar dates ("2022-03-24"); Date parses them as UTC
 *  midnight, so render in UTC too or São Paulo would show the previous day.
 *  Unrelated to the scan clock above — a calendar date names no instant, so the
 *  site's zone would be as wrong here as the viewer's. */
const UTC_DATE = { timeZone: "UTC" } as const;
function fmtFifoDate(iso: string | null): string {
  return iso == null ? "—" : new Date(iso).toLocaleDateString("pt-BR", UTC_DATE);
}

function Kpi({ label, value, loading, hint, sub }: {
  label: string; value: string | number; loading?: boolean;
  /** One short warn-coloured line under the value: a finding (ADR 372). */
  hint?: string | null;
  /** One short neutral line under the value: what the number IS, when the
   *  label alone cannot say it (ADR 372 §4). */
  sub?: string | null;
}) {
  return (
    <div className={run.kpi}>
      <div className={run.kpiLabel}>{label}</div>
      {loading
        ? <span className={`skeleton ${run.kpiSkeleton}`} aria-hidden />
        : <div className={run.kpiValue}>{value}</div>}
      {!loading && hint && <div className={run.kpiHint}>{hint}</div>}
      {!loading && sub && <div className={run.kpiSub}>{sub}</div>}
    </div>
  );
}

export function InventoryRunPage() {
  const { t } = useTranslation();
  const { runId = "" } = useParams();
  const siteId = useSiteId();
  const qc = useQueryClient();
  const [linkMapName, setLinkMapName] = useState("");

  // Incremental inventory (ADR 150): a later walk's files append into THIS run;
  // rows the run already has dedupe server-side and come back as `duplicates`.
  const appendRef = useRef<HTMLInputElement>(null);
  const [appendBusy, setAppendBusy] = useState(false);
  const [appendErr, setAppendErr] = useState<string | null>(null);
  const [appendResult, setAppendResult] =
    useState<{ added: number; duplicates: number; rejected: number; superseded: number } | null>(null);
  // ADR 153: re-scanned addresses need an operator decision — the server answers
  // 409 with the count, we hold the files and re-submit with the chosen policy.
  const [dupPrompt, setDupPrompt] =
    useState<{ files: File[]; addresses: number; sample: string[] } | null>(null);

  async function handleAppend(files: File[], policy?: DuplicatePolicy) {
    setAppendBusy(true);
    setAppendErr(null);
    setAppendResult(null);
    try {
      const r = await appendInventoryBundle(siteId, runId, files, policy);
      setDupPrompt(null);
      setAppendResult({
        added: r.added, duplicates: r.duplicates,
        rejected: r.rejectedCount, superseded: r.superseded ?? 0,
      });
      // Everything on this page derives from the run's scans — refetch it all.
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["inventory-run", siteId, runId] }),
        qc.invalidateQueries({ queryKey: ["inventory-items", siteId, runId] }),
        qc.invalidateQueries({ queryKey: ["inventory-div", siteId, runId] }),
        qc.invalidateQueries({ queryKey: ["inventory-aisles", siteId, runId] }),
        qc.invalidateQueries({ queryKey: ["inventory-changes", siteId, runId] }),
      ]);
    } catch (x) {
      if (x instanceof DuplicateAddressesError) {
        setDupPrompt({ files, addresses: x.addresses, sample: x.sample });
      } else {
        setAppendErr(x instanceof Error ? x.message : String(x));
      }
    } finally {
      setAppendBusy(false);
      if (appendRef.current) appendRef.current.value = "";
    }
  }

  // ADR 260: removing a run is destructive and global-admin only. The button is hidden
  // for everyone else rather than shown-and-403'd, and it asks in place — the design
  // system rules out window.confirm, and this reuses the append/duplicate prompt idiom.
  const navigate = useNavigate();
  const isGlobalAdmin = useSiteStore((s) => s.isGlobalAdmin);
  // ADR 409: renaming a count and appending readings are operating actions — hidden from
  // the sales persona (it presents, the field team imports) and from plain viewers.
  const canOperate = useSiteStore((s) => !s.isSales && (s.isGlobalAdmin || s.roleRank >= ROLE_RANK.supervisor));

  // The warehouse's clock, for everything this page reports about the floor
  // (ADR 265). Undefined while the site list is still loading — the formatters
  // fall back to the viewer's zone rather than rendering a dash, because on the
  // only deployment that exists the two agree, and a blank timestamp is worse
  // than a briefly local one.
  const siteTz = useSiteStore((s) => s.availableSites.find((x) => x.id === siteId)?.timezone);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const del = useMutation({
    mutationFn: () => deleteInventoryRun(siteId, runId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inventory-runs", siteId] });
      navigate("/inventory");
    },
  });

  const runQuery = useQuery({
    queryKey: ["inventory-run", siteId, runId],
    queryFn: () => getInventoryRun(siteId, runId),
    retry: 0,
  });
  const itemsQuery = useQuery({
    queryKey: ["inventory-items", siteId, runId],
    queryFn: () => getAllInventoryRunItems(siteId, runId),
    retry: 0,
  });
  const divQuery = useQuery({
    queryKey: ["inventory-div", siteId, runId],
    queryFn: () => getInventoryDivergences(siteId, runId),
    retry: 0,
  });
  // ADR 283: justify divergences. `target` = one row, or null = all outstanding
  // ("Justificar todas"). A review is metadata about the measurement — the
  // agreement % never moves; only the justified/pending split does.
  const [justifyTarget, setJustifyTarget] = useState<{ address: string; product: string } | null | "closed">("closed");
  const [justification, setJustification] = useState("");
  // ADR 364: what happens next. Recount is the one that closes itself — an appended
  // pass (ADR 150) re-reading the address into a match drops the row from the report.
  const [disposition, setDisposition] = useState<Disposition>("accepted");
  const [onlyRecount, setOnlyRecount] = useState(false);
  const review = useMutation({
    mutationFn: () => reviewDivergences(siteId, runId, justifyTarget === null
      ? { justification, all: true, disposition }
      : { justification, rows: [justifyTarget as { address: string; product: string }], disposition }),
    onSuccess: () => {
      setJustifyTarget("closed");
      setJustification("");
      void qc.invalidateQueries({ queryKey: ["inventory-div", siteId, runId] });
    },
  });
  const unreview = useMutation({
    mutationFn: (row: { address: string; product: string }) =>
      unreviewDivergences(siteId, runId, [row]),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["inventory-div", siteId, runId] }),
  });
  const aislesQuery = useQuery({
    queryKey: ["inventory-aisles", siteId, runId],
    queryFn: () => getInventoryAisleCoverage(siteId, runId),
    retry: 0,
  });
  const changesQuery = useQuery({
    queryKey: ["inventory-changes", siteId, runId],
    queryFn: () => getInventoryChanges(siteId, runId),
    retry: 0,
  });
  const passesQuery = useQuery({
    queryKey: ["inventory-passes", siteId, runId],
    queryFn: () => getInventoryPasses(siteId, runId),
    retry: 0,
  });

  const data = runQuery.data;
  const k = data?.kpis;
  const items = itemsQuery.data?.items ?? NO_ROWS;
  const divSummary = divQuery.data?.summary;
  const divRows = divQuery.data?.rows ?? NO_ROWS;
  const aisles = aislesQuery.data?.aisles ?? [];

  // Address master for the per-aisle reconciliation (ADR 137): join key is
  // address → aisleLabel; out-of-master reads bucket under "__none__".
  const warehouseId = data?.run.warehouseId;
  // ADR 275: the WMS catalogue, so this page resolves SKU-vs-label with exactly
  // the rule the /live inspector uses. Absent snapshot → empty list → alias-only.
  // ADR 383: the warehouse's snapshot series, for the reference/closing picker.
  const snapshotsQuery = useQuery({
    queryKey: ["inventory-snapshots", siteId, warehouseId],
    queryFn: () => listExpectedStockSnapshots(siteId, warehouseId!),
    enabled: !!warehouseId,
    retry: 0,
  });
  const bind = useMutation({
    mutationFn: (b: { expectedImportId: string | null; closingImportId: string | null }) =>
      bindInventoryRunSnapshots(siteId, runId, b),
    onSuccess: () => {
      // The comparison, its reviews, the aisle rollup and the run list all read the binding.
      void qc.invalidateQueries({ queryKey: ["inventory-run", siteId, runId] });
      void qc.invalidateQueries({ queryKey: ["inventory-div", siteId, runId] });
      void qc.invalidateQueries({ queryKey: ["inventory-aisles", siteId, runId] });
      void qc.invalidateQueries({ queryKey: ["inventory-runs", siteId] });
      void qc.invalidateQueries({ queryKey: ["inventory-snapshots", siteId, warehouseId] });
    },
  });
  const catalogQuery = useQuery({
    queryKey: ["inventory-products", siteId, warehouseId],
    queryFn: () => getInventoryProducts(siteId, warehouseId!),
    enabled: !!warehouseId,
    staleTime: 300_000,
    retry: false,
  });
  const inCatalog = useMemo(() => {
    const codes = new Set((catalogQuery.data ?? []).map((r) => r.product));
    return (c: string) => codes.has(c);
  }, [catalogQuery.data]);

  const locationsQuery = useQuery({
    queryKey: ["inventory-locations", siteId, warehouseId],
    queryFn: () => listStorageLocations(siteId, warehouseId!),
    enabled: !!warehouseId,
    retry: 0,
  });
  const aisleByAddress = new Map(
    (locationsQuery.data ?? [])
      .filter((l) => l.aisleLabel != null)
      .map((l) => [l.address, l.aisleLabel as string]),
  );
  const recon = divRows.length > 0 && aisleByAddress.size > 0
    ? aisleReconciliation(divRows, aisleByAddress)
    : NO_ROWS;

  // Print-only 3D rack snapshot (ADR 163): built from the run's occupancy
  // measurements (the same set the occupation section shows) and rendered to PNGs
  // that land in the PDF. The location master (fetched above) gives the side that
  // splits the aisle into its two rack rows.
  const sideByAddress = useMemo(
    () => new Map((locationsQuery.data ?? []).map((l) => [l.address, l.side])),
    [locationsQuery.data],
  );
  const [rackImages, setRackImages] = useState<string[]>([]);

  // One sort instance per table (ADR 151) — all unconditional (rules of hooks;
  // the tables themselves render conditionally).
  // Changes timeline (ADR 153): flatten each address's generation list into
  // before→after transitions; the outcome label mirrors what the chosen policy
  // did (replaced / both kept / first kept).
  const genLabel = (g: InventoryChangeGeneration): string =>
    g.readings.map((r) => r.itemStatus === "empty"
      ? t("inventory.changes.emptyReading")
      : r.itemStatus === "unidentified"
      ? t("inventory.item.unidentified")
      // ADR 275: name the item; the pallet label is provenance, not the answer.
      : `${r.item || r.product || "—"}${r.quantity != null ? ` ×${r.quantity}` : ""}`).join("; ");
  const changeRows: ChangeRowView[] = (changesQuery.data?.addresses ?? []).flatMap((a) =>
    a.generations.slice(1).map((g, i) => {
      const prev = a.generations[i];
      return {
        key: `${a.address}-${i}`,
        address: a.address,
        when: g.importedAt,
        before: genLabel(prev),
        after: genLabel(g),
        outcome: (g.active && !prev.active ? "replaced"
          : g.active && prev.active ? "both" : "ignored") as ChangeRowView["outcome"],
      };
    }));

  const rejectSort = useTableSort(k?.rejects ?? NO_ROWS, REJECT_COLS);
  const itemSort = useTableSort(items, ITEM_COLS);
  const reconSort = useTableSort(recon, RECON_COLS);
  const divSort = useTableSort(divRows, DIV_COLS);
  // ADR 233. A full-coverage run reconciles every bin, so this table rendered one
  // row per reconciled key — 14 661 of them on JSL, ~205 000 DOM nodes, and the
  // report took 12,6 s to show its own title. Two bounds, both stated on screen:
  // the matches (97% of the rows, and not what a divergence report is for) start
  // collapsed, and what remains is capped. The CSV export is untouched — the
  // table is a viewport, the export is the record.
  const [showMatches, setShowMatches] = useState(false);
  const [occShowAll, setOccShowAll] = useState(false);
  // ADR 360: the per-address detail is behind the level's own summary column —
  // the section reads as one chart, and a level opens only when asked. Keys are
  // the level number as a string ("none" for the off-grammar bucket).
  const [occOpen, setOccOpen] = useState<Set<string>>(new Set());
  const toggleOccLevel = (key: string) => setOccOpen((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const divVisible = useMemo(() => {
    const divergent = divSort.rows.filter((r) => r.status !== "match");
    const base = showMatches ? divSort.rows : divergent;
    // ADR 364: the recount list is a filter of the same report, never a second table.
    const kept = onlyRecount ? base.filter((r) => r.review?.disposition === "recount") : base;
    return {
      rows: kept.slice(0, DIV_ROW_CAP),
      total: kept.length,
      matches: divSort.rows.length - divergent.length,
      recountOpen: divergent.filter((r) => r.review?.disposition === "recount"),
    };
  }, [divSort.rows, showMatches, onlyRecount]);
  const changeSort = useTableSort(changeRows, CHANGE_COLS);

  // WMS charts (ADR 152). Composition = share of each outcome class (the same
  // statuses as the pills, validated palette). Per-aisle = problem classes
  // only, worst aisle first — including "matched" would drown the signal the
  // chart exists to show. Values stay reachable in the KPI tiles and tables;
  // tooltips only enhance.
  // A run whose export carries no quantities cannot produce a quantity divergence
  // (ADR 147/241). Drawing the class anyway puts a zero segment and a legend entry
  // next to an unlabelled bar total, which is how "0 divergências de quantidade"
  // and a total of 8 came to look like the same number (ADR 254).
  const qtyMeasurable = divSummary?.quantitiesKnown ?? false;
  const qtySegment = (value: number) =>
    qtyMeasurable
      ? [{ key: "qty_mismatch", label: t("inventory.wms.qtyMismatch"), value, color: "var(--op-warn)" }]
      : [];

  const compositionRow: ChartRow = {
    key: "composition",
    label: null,
    segments: divSummary ? [
      { key: "match", label: t("inventory.wms.matched"), value: divSummary.matched, color: "var(--op-success)" },
      ...qtySegment(divSummary.qtyMismatch),
      { key: "missing", label: t("inventory.wms.missing"), value: divSummary.missingInIc3, color: "var(--op-danger)" },
      { key: "unexpected", label: t("inventory.wms.unexpected"), value: divSummary.unexpectedInIc3, color: "var(--op-info)" },
    ] : [],
  };
  const aisleChartRows: ChartRow[] = recon
    .map((a) => ({
      key: a.aisle,
      label: a.aisle === "__none__" ? t("inventory.recon.outOfMaster") : a.aisle,
      note: fmtPct1(a.accuracyPct),
      segments: [
        ...qtySegment(a.qtyMismatch),
        { key: "missing", label: t("inventory.wms.missing"), value: a.missing, color: "var(--op-danger)" },
        { key: "unexpected", label: t("inventory.wms.unexpected"), value: a.unexpected, color: "var(--op-info)" },
      ],
    }))
    .filter((r) => chartTotal(r) > 0)
    .sort((x, y) => chartTotal(y) - chartTotal(x));

  // Aging (ADR 194): rows = age buckets, segments = the reconciliation outcome
  // in the section's palette — "old AND missing" is the signal this section
  // exists for, and it lands in the danger band of the oldest rows. Rendered
  // only when the WMS snapshot actually carries FIFO dates.
  const agingOn = hasAging(divRows);
  const agingChartRows: ChartRow[] = agingOn
    ? ageBuckets(divRows).map((b) => ({
        key: b.key,
        label: t(`inventory.aging.${b.key}`),
        note: String(b.match + b.qtyMismatch + b.missing),
        segments: [
          { key: "match", label: t("inventory.wms.matched"), value: b.match, color: "var(--op-success)" },
          ...qtySegment(b.qtyMismatch),
          { key: "missing", label: t("inventory.wms.missing"), value: b.missing, color: "var(--op-danger)" },
        ],
      }))
    : [];
  const oldestRows = agingOn ? oldestStock(divRows) : NO_ROWS;

  // Walk passes (ADR 154). Reads the scanned addresses only, so this whole
  // section answers before any map/location master is imported — unlike the
  // coverage and per-aisle-reconciliation blocks above it.
  const passes = passesQuery.data?.passes ?? NO_PASSES;
  const passAisles = passesQuery.data?.aisles ?? NO_COMPARISONS;
  const passTotals = passesQuery.data?.totals ?? null;
  const passSort = useTableSort(passes, PASS_COLS);
  const passCmpSort = useTableSort(passAisles, PASS_CMP_COLS);
  const unidentified = passesQuery.data?.unidentifiedStock ?? NO_UNIDENTIFIED;
  const unidentifiedSort = useTableSort(unidentified, UNIDENTIFIED_COLS);

  // Vertical occupancy (ADR 162): the height sensor's per-bin fill, grouped by
  // Nível. The backend already sorts by level then fullest-first, so one linear
  // pass collapses the contiguous runs — a null level (off-grammar address)
  // sinks to its own trailing bucket.
  const occ = passesQuery.data?.occupancyMeasurements ?? NO_OCC;
  const occScanned = data?.run.addressScanned ?? new Set(items.map((r) => r.address)).size;
  // One global scale for every slot so a taller bin draws a longer track — the
  // clear-height difference across levels is visible, not normalised away per
  // level (slots within a level are usually the same height). The blue fill is a
  // fraction of its slot, so its absolute length then tracks the load height on
  // that same global scale.
  const occMaxBin = Math.max(1, ...occ.map((m) => m.binHeightMm ?? 0));
  // ADR 300: how many of these the twin divided itself. Non-zero and the section says so.
  const occDerived = occ.reduce((n, m) => n + (m.derived ? 1 : 0), 0);
  const occByLevel: Array<[number | null, InventoryOccupancyMeasurement[]]> = [];
  for (const m of occ) {
    const last = occByLevel[occByLevel.length - 1];
    if (last && last[0] === m.level) last[1].push(m);
    else occByLevel.push([m.level, [m]]);
  }
  // Per-level roll-up for the summary chart (occupancy per level): the mean of
  // the kit's occupancyPct over the level's measured bins — the same "média" the
  // detail group headers show, so the resume and the detail never disagree. All
  // bins in a level share a clear height, so this equals Σload/Σbin there.
  // ADR 233: one bar per measured bin. A full-coverage run measures thousands of
  // them (the rack sensor samples ~30% of reads), and this chart alone was 46 534
  // DOM nodes — 91% of the report. The per-level summary above each chart already
  // carries the whole level's count and average, so the bars are a sample to read,
  // not a ledger; the full set is one click away and always in the CSV.
  const occGroups = occByLevel.map(([level, rows]) => ({
    level,
    rows: occShowAll ? rows : rows.slice(0, OCC_ROWS_PER_LEVEL),
    hidden: occShowAll ? 0 : Math.max(0, rows.length - OCC_ROWS_PER_LEVEL),
    count: rows.length,
    avgPct: rows.reduce((sum, r) => sum + r.occupancyPct, 0) / rows.length,
  }));

  // Generate the print-only rack snapshot from the same measurements (ADR 163).
  useEffect(() => {
    const layouts = occ.length ? planRackImages(occ, (a) => sideByAddress.get(a) ?? null) : [];
    let cancelled = false;
    if (layouts.length === 0) {
      // Deferred like the async branch below — a synchronous setState inside an
      // effect body is an error under the v7 hook rules (ADR 136).
      queueMicrotask(() => { if (!cancelled) setRackImages([]); });
      return () => { cancelled = true; };
    }
    // Lazy-load the three.js renderer so it (and three) stay out of the report's
    // main chunk — only runs that actually print a rack pay to download it.
    void import("../scene/renderRackSnapshot")
      .then(({ renderRackSnapshot }) => {
        if (cancelled) return;
        try {
          setRackImages(layouts.map((l) => renderRackSnapshot(l)).filter(Boolean));
        } catch {
          setRackImages([]); // no WebGL (headless / unsupported) — degrade to no snapshot
        }
      })
      .catch(() => { if (!cancelled) setRackImages([]); });
    return () => { cancelled = true; };
  }, [occ, sideByAddress]);

  // Two counts, one pass per level (ADR 159). The backend assigns passes and
  // reconciles per location; these just pivot for the summary and tally the
  // headline. Sort hook is unconditional (rules of hooks) — the section renders
  // conditionally on twoCounts.columns below.
  const twoCounts = passesQuery.data?.twoCounts;
  const levelSummary = summarizeLevels(twoCounts?.columns ?? []);
  const reconTones = tallyTones(twoCounts?.rows ?? NO_RECON);
  const countReconSort = useTableSort(twoCounts?.rows ?? NO_RECON, RECON_ROW_COLS);
  // Two counts need two passes. When EVERY level was walked once, both columns
  // are the same pass (`duplicated`), so the reconciliation compares a pass with
  // itself: 0 agree, 0 differ, everything duplicated, and an address-by-address
  // table whose two columns are identical by construction. That is a tautology
  // rendered as three KPIs and N rows, not a finding — the section stays away
  // until some level actually carries an independent second pass (ADR 252).
  const hasIndependentCounts = !!twoCounts && twoCounts.columns.some((c) => !c.duplicated);

  // What a pass traverses (ADR 253). Under a grammar that separates them, the
  // aisle alone names a whole rack — "JY" for every level of Rua JY — so the
  // level has to show or four rows read as four copies of one place. Rendered as
  // "JY · Nível 2" rather than re-fusing them into "JY02": that token is one
  // warehouse's convention, not a fact about every address scheme.
  const segmentLabel = (aisle: string | null, level: number | null): string =>
    aisle == null ? "—"
    : level == null ? aisle
    : t("inventory.passes.segment", { aisle, level });
  // null = the count never reached the bin (not "visited and empty"); "" = empty.
  const countCell = (v: string | null): string =>
    v == null ? t("inventory.counts.notReached") : v === "" ? t("inventory.counts.empty") : v;

  // Part-to-whole across the four ways two passes can land on one bin. The amber
  // band is the point of the section: bins a single pass would have gotten wrong.
  const passChartRows: ChartRow[] = passAisles.map((c) => ({
    key: `${c.aisle ?? "—"}/${c.level ?? ""}`,
    label: segmentLabel(c.aisle, c.level),
    note: c.agreementPct == null ? undefined : fmtPct1(c.agreementPct),
    segments: [
      { key: "agreeLabel", label: t("inventory.passes.agreeLabel"), value: c.agreeLabel, color: "var(--op-success)" },
      { key: "agreeEmpty", label: t("inventory.passes.agreeEmpty"), value: c.agreeEmpty, color: "var(--op-info)" },
      { key: "onePassOnly", label: t("inventory.passes.onePassOnly"), value: c.onePassOnly, color: "var(--op-warn)" },
      { key: "conflict", label: t("inventory.passes.conflict"), value: c.labelConflict, color: "var(--op-danger)" },
    ],
  })).filter((r) => chartTotal(r) > 0);

  // ADR 364: the recount list is the open recount rows of the same report, as a sheet
  // the person walking the aisle can carry — one row per address to revisit.
  const exportRecountCsv = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    downloadTextFile(`recontagem-${(data?.run.name ?? runId).replace(/\s+/g, "_")}-${stamp}.csv`,
      divergencesToCsv(divVisible.recountOpen), "text/csv;charset=utf-8");
  };
  // ADR 423: where each pallet ended up — address, label, quantity, last read.
  const exportPallets = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    downloadTextFile(`enderecos-${(data?.run.name ?? runId).replace(/\s+/g, "_")}-${stamp}.csv`,
      addressPalletsToCsv(items, siteTz), "text/csv", true);
  };
  const exportCsv = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    // Export follows the on-screen order — a sorted table downloads sorted.
    // ADR 374: a workbook of the divergences only, each with a sentence saying
    // what is wrong — the sheet that goes to whoever fixes the WMS or the bin.
    downloadBinaryFile(`divergencias-${(data?.run.name ?? runId).replace(/\s+/g, "_")}-${stamp}.xlsx`,
                       divergencesToXlsx(divSort.rows, data?.run.name ?? runId),
                       "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
  };

  // The customer-facing sheet (ADR 241). Its own endpoint, not the divergence
  // rows: that reconciliation is per (address, item) and needs the label→item
  // translation to join, while this report asks the address-level question the
  // audit sheet asks. Fetched on click — a report nobody exports should not cost
  // every visitor a query.
  const [flexBusy, setFlexBusy] = useState(false);
  const [flexError, setFlexError] = useState(false);
  const exportFlex = async () => {
    setFlexBusy(true);
    setFlexError(false);
    try {
      const report = await getInventoryStockReport(siteId, runId);
      const stamp = new Date().toISOString().slice(0, 10);
      // .xlsx, not .csv (ADR 264): the customer filters and sorts this sheet, and
      // a CSV gives them text in every column — an aging date that sorts
      // alphabetically is worse than useless on a report about aging.
      downloadBinaryFile(`relatorio-${(data?.run.name ?? runId).replace(/\s+/g, "_")}-${stamp}.xlsx`,
                         flexReportToXlsx(report.rows, report.warehouseLabel),
                         "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
    } catch {
      setFlexError(true);
    } finally {
      setFlexBusy(false);
    }
  };

  // Rename (ADR 232). The title is what a run is called in the list, in the /live
  // picker and on a printed report, so the edit lives on the title itself.
  const [renameDraft, setRenameDraft] = useState<string | null>(null);
  const rename = useMutation({
    mutationFn: (name: string) => renameInventoryRun(siteId, runId, name),
    onSuccess: () => {
      setRenameDraft(null);
      void qc.invalidateQueries({ queryKey: ["inventory-run", siteId, runId] });
      // The list and the /live run picker read the same key — both show the title.
      void qc.invalidateQueries({ queryKey: ["inventory-runs", siteId] });
    },
  });

  // Run↔map link repair (ADR 140): only relevant while coverage can't resolve,
  // so the maps query stays off on healthy reports.
  const coverageUnresolved = !!k && k.coveragePct == null;
  const mapsQuery = useQuery({
    queryKey: ["inventory-maps", siteId],
    queryFn: () => listInventoryMaps(siteId),
    enabled: coverageUnresolved,
    retry: 0,
  });
  const linkableMaps = (mapsQuery.data ?? []).filter((m) => m.warehouseId === data?.run.warehouseId);
  const linkMap = useMutation({
    mutationFn: (mapName: string) => updateInventoryRunMap(siteId, runId, mapName),
    onSuccess: () => {
      // Everything map-derived recomputes: KPIs/skipped (run), aisle bars, recon join.
      void qc.invalidateQueries({ queryKey: ["inventory-run", siteId, runId] });
      void qc.invalidateQueries({ queryKey: ["inventory-aisles", siteId, runId] });
      void qc.invalidateQueries({ queryKey: ["inventory-locations", siteId, warehouseId] });
    },
  });

  // A failed run query must never fall through to the KPI/Alertas sections:
  // with data undefined, the alerts panel would otherwise render the "no
  // rejections" happy path even though the load actually failed.
  if (runQuery.isError || (!runQuery.isLoading && !data)) {
    return (
      <div className={styles.page}>
        <h1 className={styles.h1}>{runId.slice(0, 8)}</h1>
        <p role="alert" className={styles.error}>{t("inventory.run.error")}</p>
      </div>
    );
  }

  return (
    <div className={`${styles.page} ${run.report}`}>
      <div className={run.titleRow}>
        {renameDraft === null ? (
          <h1 className={styles.h1}>{data?.run.name ?? runId.slice(0, 8)}</h1>
        ) : (
          <form
            className={run.renameForm}
            onSubmit={(e) => { e.preventDefault(); rename.mutate(renameDraft.trim()); }}
          >
            <input
              className={run.renameInput}
              value={renameDraft}
              maxLength={120}
              autoFocus
              disabled={rename.isPending}
              onChange={(e) => setRenameDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Escape") setRenameDraft(null); }}
              aria-label={t("inventory.rename.label")}
            />
            <button type="submit" className={run.actionBtnPrimary} disabled={rename.isPending}>
              {t("inventory.rename.save")}
            </button>
            <button type="button" className={run.actionBtn} disabled={rename.isPending}
                    onClick={() => setRenameDraft(null)}>
              {t("inventory.rename.cancel")}
            </button>
          </form>
        )}
        <div className={run.titleActions}>
          {canOperate && renameDraft === null && (
            <button type="button" className={`${run.actionBtn} ${run.noPrint}`}
                    onClick={() => setRenameDraft(data?.run.name ?? "")}>
              {t("inventory.rename.action")}
            </button>
          )}
          {canOperate && <label className={`${run.actionBtn} ${run.noPrint}`}>
            <input
              ref={appendRef}
              type="file"
              accept=".csv,text/csv"
              multiple
              hidden
              disabled={appendBusy}
              onChange={(e) => {
                const fs = Array.from(e.target.files ?? []);
                if (fs.length > 0) void handleAppend(fs);
              }}
            />
            <span>{appendBusy ? t("inventory.append.busy") : t("inventory.append.action")}</span>
          </label>}
          <button type="button" className={`${run.actionBtn} ${run.noPrint}`}
                  onClick={() => window.print()}>
            {t("inventory.report.print")}
          </button>
          {isGlobalAdmin && renameDraft === null && (
            <button type="button" className={`${run.dangerBtn} ${run.noPrint}`}
                    onClick={() => setConfirmDelete(true)} disabled={del.isPending}>
              {t("inventory.delete.action")}
            </button>
          )}
        </div>
      </div>
      {confirmDelete && (
        <div className={run.deleteDialog} role="alertdialog" aria-label={t("inventory.delete.action")}
             data-testid="delete-confirm">
          <p className={run.dupTitle}>
            {t("inventory.delete.confirm", {
              name: data?.run.name ?? runId.slice(0, 8),
              count: k?.addressesScanned ?? 0,
            })}
          </p>
          <p className={run.dupHint}>{t("inventory.delete.keepsArchive")}</p>
          {del.isError && <p className={run.alertBad} role="alert">{t("inventory.delete.failed")}</p>}
          <div className={run.dupActions}>
            <button type="button" className={run.dangerBtn} disabled={del.isPending}
                    onClick={() => del.mutate()}>
              {del.isPending ? t("inventory.delete.busy") : t("inventory.delete.confirmAction")}
            </button>
            <button type="button" className={run.actionBtn} disabled={del.isPending}
                    onClick={() => setConfirmDelete(false)}>
              {t("inventory.append.dup.cancel")}
            </button>
          </div>
        </div>
      )}
      {appendResult && (
        <p className={run.note} role="status">
          {t("inventory.append.done", appendResult)}
          {appendResult.superseded > 0 &&
            ` ${t("inventory.append.doneSuperseded", { count: appendResult.superseded })}`}
        </p>
      )}
      {rename.isError && <p role="alert" className={styles.error}>{t("inventory.rename.error")}</p>}
      {appendErr && <p role="alert" className={styles.error}>{appendErr}</p>}
      {dupPrompt && (
        <div className={`${run.dupDialog} ${run.noPrint}`} role="alertdialog" aria-labelledby="dup-title">
          <p id="dup-title" className={run.dupTitle}>
            {t("inventory.append.dup.title", { count: dupPrompt.addresses })}
          </p>
          <p className={run.dupHint}>
            {t("inventory.append.dup.hint")}{" "}
            <span className={styles.mono}>
              {dupPrompt.sample.slice(0, 5).join(", ")}{dupPrompt.addresses > 5 ? "…" : ""}
            </span>
          </p>
          <div className={run.dupActions}>
            <button type="button" className={run.actionBtnPrimary} disabled={appendBusy}
                    onClick={() => void handleAppend(dupPrompt.files, "keep_latest")}>
              {t("inventory.append.dup.keepLatest")}
            </button>
            <button type="button" className={run.actionBtn} disabled={appendBusy}
                    onClick={() => void handleAppend(dupPrompt.files, "keep_first")}>
              {t("inventory.append.dup.keepFirst")}
            </button>
            <button type="button" className={run.actionBtn} disabled={appendBusy}
                    onClick={() => void handleAppend(dupPrompt.files, "add_both")}>
              {t("inventory.append.dup.addBoth")}
            </button>
            <button type="button" className={run.actionBtn} disabled={appendBusy}
                    onClick={() => setDupPrompt(null)}>
              {t("inventory.append.dup.cancel")}
            </button>
          </div>
        </div>
      )}
      {/* Printed-page header (ADR 137): context that lives in the app chrome on
          screen (site/warehouse/map) must travel with the paper copy. */}
      {data && (
        <div className={run.printHeader}>
          <div>{t("inventory.report.site")}: <b>{siteId}</b> · {t("inventory.report.warehouse")}: <b>{data.run.warehouseId}</b>{data.run.mapName ? <> · {t("inventory.report.map")}: <b>{data.run.mapName}</b></> : null}</div>
          <div>{t("inventory.report.generated", { ts: new Date().toLocaleString("pt-BR") })}
            {siteTz ? <> · {t("inventory.report.clock", { tz: siteTz })}</> : null}</div>
        </div>
      )}
      <p className={styles.sub}>
        {runQuery.isLoading
          ? <span className="skeleton" style={{ width: 140, height: 16 }} />
          : data && (
              <>
                <span className={`${styles.pill} ${STATUS_PILL[data.run.status]}`}>
                  {t(`inventory.status.${data.run.status}`)}
                </span>
                {" · "}
                {new Date(data.run.createdAt).toLocaleString("pt-BR")}
                {/* ADR 266: name the clock. Every time this page reports about the
                    FLOOR is on the site's zone, and a timestamp whose zone is not
                    written down cannot be checked by the person reading it — which
                    is how a three-hour shift survived a month (ADR 262). */}
                {siteTz && (
                  <span data-testid="run-clock-note">
                    {" · "}{t("inventory.report.clock", { tz: siteTz })}
                  </span>
                )}
              </>
            )}
      </p>

      <section className={run.kpis} data-testid="run-kpis">
        <Kpi label={t("inventory.kpi.skus")} value={k?.skus ?? "—"} loading={runQuery.isLoading} />
        <Kpi label={t("inventory.kpi.units")} value={k?.units ?? "—"} loading={runQuery.isLoading} />
        {/* ADR 372: two different facts, named as such. "Lidos" is every
            distinct address the kit read; "no cadastro" is the master the
            coverage divides by. A run can read MORE than the map has — those
            reads are the map's gap, and the hint says how many. */}
        <Kpi
          label={t("inventory.kpi.addresses")}
          value={k?.addressesScanned ?? "—"}
          loading={runQuery.isLoading}
          // ADR 410: the number alone read as "addresses with stock" or as the row
          // count; the split says what a visited address was.
          hint={k ? [
            k.addressesWithStock != null ? t("inventory.kpi.addressesSplit", {
              stock: k.addressesWithStock,
              empty: k.addressesScanned - k.addressesWithStock - (k.addressesUnidentified ?? 0),
              unidentified: k.addressesUnidentified ?? 0,
            }) : null,
            k.addressesOutOfMaster ? t("inventory.kpi.outOfMaster", { count: k.addressesOutOfMaster }) : null,
          ].filter(Boolean).join(" · ") || null : null}
        />
        <Kpi label={t("inventory.kpi.addressTotal")} value={k?.addressTotal ?? "—"} loading={runQuery.isLoading} />
        <Kpi
          label={t("inventory.kpi.coverage")}
          value={k && k.coveragePct != null ? fmtPct1(k.coveragePct) : "—"}
          loading={runQuery.isLoading}
        />
      </section>

      {/* Honest footnote: coverage % needs the address master (ADR 122) to know
          the denominator — never fake a coverage ratio. Once a master resolves
          (coveragePct non-null), the tiles above and the panel below carry the
          real numbers, so repeating the caveat would be stale. When the run HAS
          a map name that didn't resolve, say so by name (ADR 139) — the generic
          note hides a typo'd join. Either way, when the warehouse has imported
          maps, offer the link repair right here (ADR 140) instead of sending
          the operator back to re-upload. */}
      {coverageUnresolved && (
        <div className={run.note}>
          {data?.run.mapName
            ? <p role="alert" style={{ margin: 0 }}>{t("inventory.note.mapUnresolved", { name: data.run.mapName })}</p>
            : <p style={{ margin: 0 }}>
                {t("inventory.note.noMaster")}{" "}
                {/* Address authoring moved to the warehouse structure page (ADR 142). */}
                <Link to="/admin/warehouses" className={run.noPrint}>{t("inventory.note.noMasterAction")}</Link>
              </p>}
          {linkableMaps.length > 0 && (
            <div className={`${run.linkMapRow} ${run.noPrint}`}>
              <select
                className={run.linkMapSelect}
                value={linkMapName}
                onChange={(e) => setLinkMapName(e.target.value)}
                aria-label={t("inventory.linkMap.label")}
                disabled={linkMap.isPending}
              >
                <option value="">{t("inventory.linkMap.placeholder")}</option>
                {linkableMaps.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}
              </select>
              <button
                type="button"
                className={run.actionBtn}
                disabled={!linkMapName || linkMap.isPending}
                onClick={() => linkMap.mutate(linkMapName)}
              >
                {t("inventory.linkMap.action")}
              </button>
              {linkMap.isError && <span className={run.linkMapError} role="alert">{t("inventory.linkMap.error")}</span>}
            </div>
          )}
        </div>
      )}

      {k && k.coveragePct != null && (
        <CollapsibleSection id="run.skipped" title={t("inventory.skipped.title")}>
          {!k.skippedCount ? (
            <p className={run.alertOk}>{t("inventory.skipped.none")}</p>
          ) : (
            <details className={run.skippedDetails}>
              <summary className={run.skippedSummary}>
                {t("inventory.skipped.count", { count: k.skippedCount })}
              </summary>
              <ul className={run.skippedList}>
                {(k.skipped ?? []).map((addr) => (
                  <li key={addr} className={styles.mono}>{addr}</li>
                ))}
              </ul>
            </details>
          )}
        </CollapsibleSection>
      )}

      {/* Per-aisle coverage (ADR 122 slice 3): same map-resolved gate as the
          skipped-addresses panel above, plus a non-empty aisles payload —
          hidden otherwise rather than rendering an empty chart. */}
      {k && k.coveragePct != null && aisles.length > 0 && (
        <CollapsibleSection id="run.aisles" title={t("inventory.aisles.title")}>
          {/* Same hand-rolled bar-list grammar as the Top-10 SKUs chart below
              (run.skuChart/skuRow/skuLabel/skuTrack/skuFill/skuValue) — the
              value cell stacks coveragePct over scanned/total instead of a
              single quantity. */}
          <div className={run.skuChart}>
            {aisles.map((a) => (
              <div key={a.aisle} className={run.skuRow}>
                <div className={`${run.skuLabel} ${styles.mono}`}>
                  {a.aisle === "__none__" ? t("inventory.aisle.none") : a.aisle}
                </div>
                <div className={run.skuTrack}>
                  <div className={run.skuFill} style={{ width: `${a.coveragePct}%` }} />
                </div>
                <div className={`${run.skuValue} ${styles.mono}`}>
                  <div>{fmtPct1(a.coveragePct)}</div>
                  <div>{a.scanned}/{a.total}</div>
                </div>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* ADR 259: the run's trail + timeline — the first reader of the pose and
          event planes. Placed right after the per-aisle coverage it explains: the
          bar says 60 %, the trail shows which aisle the kit never entered. */}
      {data && (
        <RunTrailSection
          siteId={siteId}
          warehouseId={data.run.warehouseId}
          runId={runId}
          timeZone={siteTz}
          aisles={aisles}
          mapName={data.run.mapName ?? null}
        />
      )}

      <CollapsibleSection id="run.alerts" title={t("inventory.alerts.title")}>
        {k && k.rejectedCount > 0 ? (
          <>
            <p role="alert" className={run.alertBad}>{t("inventory.alerts.rejected", { count: k.rejectedCount })}</p>
            {/* W17: each rejected line with its raw content + reason — a count
                without the lines is a silent drop with extra steps. The list is
                capped server-side (50); the footnote keeps the total honest. */}
            {(k.rejects ?? []).length > 0 && (
              <div className={styles.tableScroll}>
                <table className={styles.table} data-testid="rejects-table">
                  <thead>
                    <tr>
                      <SortableTh label={t("inventory.rejects.col.reason")} sortKey="reason" sort={rejectSort.sort} onToggle={rejectSort.toggle} />
                      <SortableTh label={t("inventory.rejects.col.line")} sortKey="line" sort={rejectSort.sort} onToggle={rejectSort.toggle} />
                    </tr>
                  </thead>
                  <tbody>
                    {rejectSort.rows.map((r, i) => (
                      <tr key={`${r.line}-${i}`}>
                        <td>{rejectReasonLabel(r.reason, t)}</td>
                        <td className={styles.mono}>{r.line}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {k.rejects && k.rejectedCount > k.rejects.length && (
              <p className={run.note}>
                {t("inventory.rejects.capped", { shown: k.rejects.length, total: k.rejectedCount })}
              </p>
            )}
          </>
        ) : (
          <p className={run.alertOk}>{t("inventory.alerts.none")}</p>
        )}
      </CollapsibleSection>

      {k && k.topSkus.length > 0 && (
        <CollapsibleSection id="run.topSkus" title={t("inventory.topSkus")}>
          {/* No ECharts (or any chart lib) wrapper exists yet anywhere in web/src
              — this mirrors OeePage's own hand-rolled bar-list grammar instead
              of a bare <ul>, staying dependency-free but design-system-true. */}
          <div className={run.skuChart}>
            {(() => {
              const maxQty = Math.max(...k.topSkus.map((s) => s.quantity));
              return k.topSkus.map((s) => (
                <div key={s.product} className={run.skuRow}>
                  <div className={`${run.skuLabel} ${styles.mono}`}>{s.product}</div>
                  <div className={run.skuTrack}>
                    <div className={run.skuFill} style={{ width: `${(s.quantity / maxQty) * 100}%` }} />
                  </div>
                  <div className={`${run.skuValue} ${styles.mono}`}>{s.quantity}</div>
                </div>
              ));
            })()}
          </div>
        </CollapsibleSection>
      )}

      {/* The raw reading list: longest table on the page and the one most often
          scrolled past, so it starts folded (ADR 252). */}
      <CollapsibleSection id="run.items" title={t("inventory.items.title")} defaultOpen={false}>
        {items.length > 0 && (
          <div className={`${run.sectionHead} ${run.noPrint}`}>
            <button type="button" className={run.actionBtn} onClick={exportPallets}>
              {t("inventory.items.exportCsv")}
            </button>
          </div>
        )}
        <div className={styles.tableScroll}>
          <table className={styles.table} data-testid="items-table">
            <thead>
              <tr>
                <SortableTh label={t("inventory.items.col.address")} sortKey="address" sort={itemSort.sort} onToggle={itemSort.toggle} />
                <SortableTh label={t("inventory.items.col.sku")} sortKey="item" sort={itemSort.sort} onToggle={itemSort.toggle} />
                <SortableTh label={t("inventory.items.col.label")} sortKey="product" sort={itemSort.sort} onToggle={itemSort.toggle} />
                <SortableTh label={t("inventory.items.col.quantity")} sortKey="quantity" sort={itemSort.sort} onToggle={itemSort.toggle} />
                <SortableTh label={t("inventory.items.col.status")} sortKey="status" sort={itemSort.sort} onToggle={itemSort.toggle} />
                <SortableTh label={t("inventory.items.col.scanTs")} sortKey="scanTs" sort={itemSort.sort} onToggle={itemSort.toggle} />
              </tr>
            </thead>
            <tbody>
              {itemsQuery.isLoading
                ? Array.from({ length: 3 }, (_, i) => (
                    <tr key={i}>
                      {[90, 100, 90, 60, 80, 130].map((w, j) => (
                        <td key={j}><span className="skeleton" style={{ width: w, height: 14, display: "block" }} /></td>
                      ))}
                    </tr>
                  ))
                : itemsQuery.isError
                  ? <tr><td colSpan={6} className={styles.error}>{t("inventory.items.error")}</td></tr>
                  : items.length === 0
                    ? <tr><td colSpan={6} className={styles.empty}>{t("inventory.items.empty")}</td></tr>
                    : itemSort.rows.map((r, i) => (
                        <tr key={`${r.address}-${r.product}-${i}`}>
                          <td className={styles.mono}>{r.address}</td>
                          <td className={styles.mono}>
                            {resolveSku(r.product, r.item, inCatalog) ?? "—"}
                          </td>
                          <td className={styles.mono}>{r.product || "—"}</td>
                          <td className={styles.mono}>{r.quantity ?? "—"}</td>
                          <td>{t(`inventory.item.${r.status}`)}</td>
                          <td className={styles.mono}>{r.scanTs ? fmtScanDateTime(r.scanTs, siteTz) : "—"}</td>
                        </tr>
                      ))}
            </tbody>
          </table>
        </div>
      </CollapsibleSection>

      {/* Walk passes (ADR 154). An aisle walked out and back was read twice by
          two independent passes; this is what that redundancy bought. Derived
          from the scanned addresses, so it renders with no map imported. */}
      {passes.length > 0 && (
        <CollapsibleSection id="run.passes" title={t("inventory.passes.title")} data-testid="passes-section">
          {/* The standard intro explains what the out-and-back redundancy bought.
              On a run where no level was walked twice it would be describing a
              comparison the page is not showing, so the copy narrows to what a
              pass IS (ADR 252). */}
          <p className={run.note}>
            {t(hasIndependentCounts ? "inventory.passes.intro" : "inventory.passes.introSingle")}
          </p>

          {passTotals && passTotals.forwardRecallPct != null && passTotals.backRecallPct != null && (
            <section className={run.kpis}>
              <Kpi
                label={t("inventory.passes.kpi.forwardRecall")}
                value={fmtPct1(passTotals.forwardRecallPct)}
              />
              <Kpi
                label={t("inventory.passes.kpi.backRecall")}
                value={fmtPct1(passTotals.backRecallPct)}
              />
              <Kpi label={t("inventory.passes.kpi.union")} value={passTotals.unionLabels} />
              <Kpi
                label={t("inventory.passes.kpi.estimatedMissed")}
                value={passTotals.estimatedMissedLabels ?? "—"}
              />
            </section>
          )}

          <div className={styles.tableScroll}>
            <table className={styles.table} data-testid="passes-table">
              <thead>
                <tr>
                  <SortableTh label={t("inventory.passes.col.file")} sortKey="file" sort={passSort.sort} onToggle={passSort.toggle} />
                  <SortableTh label={t("inventory.passes.col.aisle")} sortKey="aisle" sort={passSort.sort} onToggle={passSort.toggle} />
                  <SortableTh label={t("inventory.passes.col.direction")} sortKey="direction" sort={passSort.sort} onToggle={passSort.toggle} />
                  <SortableTh label={t("inventory.passes.col.started")} sortKey="started" sort={passSort.sort} onToggle={passSort.toggle} />
                  <SortableTh label={t("inventory.passes.col.readings")} sortKey="readings" sort={passSort.sort} onToggle={passSort.toggle} />
                  <SortableTh label={t("inventory.passes.col.labels")} sortKey="labels" sort={passSort.sort} onToggle={passSort.toggle} />
                </tr>
              </thead>
              <tbody>
                {passSort.rows.map((p) => (
                  <tr key={p.bundleId}>
                    <td className={styles.mono}>{p.fileName}</td>
                    <td className={styles.mono}>{segmentLabel(p.aisle, p.level)}</td>
                    <td>{t(`inventory.passes.direction.${p.direction}`)}</td>
                    <td className={styles.mono}>{p.startedAt ? fmtScanTime(p.startedAt, siteTz) : "—"}</td>
                    <td className={styles.mono}>{p.readings}</td>
                    <td className={styles.mono}>{p.labels}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Two counts, one pass per level (ADR 159). Presents the same walk as
              two independent inventories reconciled side by side — Nível 1's
              single pass duplicated into both — rather than one fused number. */}
          {twoCounts && hasIndependentCounts && (
            <div data-testid="two-counts-section">
              <h3 className={run.h3}>{t("inventory.counts.title")}</h3>
              <p className={run.note}>{t("inventory.counts.intro")}</p>

              <section className={run.kpis}>
                <Kpi label={t("inventory.counts.kpi.agree")} value={reconTones.agree} />
                <Kpi label={t("inventory.counts.kpi.differ")} value={reconTones.differ} />
                <Kpi label={t("inventory.counts.kpi.duplicated")} value={reconTones.dup} />
              </section>

              <h4 className={run.h3}>{t("inventory.counts.summaryTitle")}</h4>
              <div className={styles.tableScroll}>
                <table className={styles.table} data-testid="two-counts-summary">
                  <thead>
                    <tr>
                      <th>{t("inventory.counts.col.level")}</th>
                      <th>{t("inventory.counts.count1")} · {t("inventory.counts.col.direction")}</th>
                      <th>{t("inventory.counts.count1")} · {t("inventory.counts.col.labels")}</th>
                      <th>{t("inventory.counts.count2")} · {t("inventory.counts.col.direction")}</th>
                      <th>{t("inventory.counts.count2")} · {t("inventory.counts.col.labels")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {levelSummary.map((s) => (
                      <tr key={s.aisle}>
                        <td className={styles.mono}>
                          {segmentLabel(s.aisle, s.level)}
                          {s.duplicated && <> ({t("inventory.counts.duplicatedTag")})</>}
                        </td>
                        <td>{s.c1 ? t(`inventory.passes.direction.${s.c1.direction}`) : "—"}</td>
                        <td className={styles.mono}>{s.c1?.labels ?? "—"}</td>
                        <td>{s.c2 ? t(`inventory.passes.direction.${s.c2.direction}`) : "—"}</td>
                        <td className={styles.mono}>{s.c2?.labels ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h4 className={run.h3}>{t("inventory.counts.reconTitle")}</h4>
              <p className={run.note}>{t("inventory.counts.reconIntro")}</p>
              <div className={styles.tableScroll}>
                <table className={styles.table} data-testid="two-counts-recon">
                  <thead>
                    <tr>
                      <SortableTh label={t("inventory.counts.col.address")} sortKey="address" sort={countReconSort.sort} onToggle={countReconSort.toggle} />
                      <SortableTh label={t("inventory.counts.count1")} sortKey="count1" sort={countReconSort.sort} onToggle={countReconSort.toggle} />
                      <SortableTh label={t("inventory.counts.count2")} sortKey="count2" sort={countReconSort.sort} onToggle={countReconSort.toggle} />
                      <SortableTh label={t("inventory.counts.col.situation")} sortKey="situation" sort={countReconSort.sort} onToggle={countReconSort.toggle} />
                    </tr>
                  </thead>
                  <tbody>
                    {countReconSort.rows.map((r) => (
                      <tr key={r.address} className={situationTone(r.situation) === "differ" ? run.reconDiffer : undefined}>
                        <td className={styles.mono}>{r.address}</td>
                        <td className={styles.mono}>{countCell(r.count1)}</td>
                        <td className={styles.mono}>{countCell(r.count2)}</td>
                        <td>
                          <span className={`${styles.pill} ${SITUATION_PILL[r.situation]}`}>
                            {t(`inventory.counts.situation.${r.situation}`)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {passChartRows.length > 0 && (
            <>
              <h3 className={run.h3}>{t("inventory.passes.compareTitle")}</h3>
              <p className={run.note}>{t("inventory.passes.compareIntro")}</p>
              <div className={run.chartSection} data-testid="passes-chart">
                <StackedBarChart rows={passChartRows} normalized totalLabel={t("inventory.passes.totalBins")} />
              </div>
              <div className={styles.tableScroll}>
                <table className={styles.table} data-testid="pass-comparison-table">
                  <thead>
                    <tr>
                      <SortableTh label={t("inventory.passes.col.aisle")} sortKey="aisle" sort={passCmpSort.sort} onToggle={passCmpSort.toggle} />
                      <SortableTh label={t("inventory.passes.col.compared")} sortKey="compared" sort={passCmpSort.sort} onToggle={passCmpSort.toggle} />
                      <SortableTh label={t("inventory.passes.col.agreement")} sortKey="agreement" sort={passCmpSort.sort} onToggle={passCmpSort.toggle} />
                      <SortableTh label={t("inventory.passes.onePassOnly")} sortKey="onePassOnly" sort={passCmpSort.sort} onToggle={passCmpSort.toggle} />
                      <SortableTh label={t("inventory.passes.conflict")} sortKey="conflict" sort={passCmpSort.sort} onToggle={passCmpSort.toggle} />
                      <SortableTh label={t("inventory.passes.col.union")} sortKey="union" sort={passCmpSort.sort} onToggle={passCmpSort.toggle} />
                      <SortableTh label={t("inventory.passes.col.forwardRecall")} sortKey="forwardRecall" sort={passCmpSort.sort} onToggle={passCmpSort.toggle} />
                      <SortableTh label={t("inventory.passes.col.backRecall")} sortKey="backRecall" sort={passCmpSort.sort} onToggle={passCmpSort.toggle} />
                      <SortableTh label={t("inventory.passes.col.missed")} sortKey="missed" sort={passCmpSort.sort} onToggle={passCmpSort.toggle} />
                    </tr>
                  </thead>
                  <tbody>
                    {passCmpSort.rows.map((c) => (
                      <tr key={`${c.aisle ?? "—"}/${c.level ?? ""}`}>
                        <td className={styles.mono}>{segmentLabel(c.aisle, c.level)}</td>
                        <td className={styles.mono}>{c.addressesCompared}</td>
                        <td className={styles.mono}>{c.agreementPct == null ? "—" : fmtPct1(c.agreementPct)}</td>
                        <td className={styles.mono}>{c.onePassOnly}</td>
                        <td className={styles.mono}>{c.labelConflict}</td>
                        <td className={styles.mono}>{c.unionLabels}</td>
                        <td className={styles.mono}>{c.forwardRecallPct == null ? "—" : fmtPct1(c.forwardRecallPct)}</td>
                        <td className={styles.mono}>{c.backRecallPct == null ? "—" : fmtPct1(c.backRecallPct)}</td>
                        <td className={styles.mono}>{c.estimatedMissedLabels ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className={run.note}>{t("inventory.passes.estimateNote")}</p>
            </>
          )}

          {/* Bins holding something that no pass could name — measured occupied by
              the height sensor, or read as `unidentified` by the camera (ADR 263).
              Unlike everything above, this is not a statistic: it is a list of
              physical locations someone has to go and look at. */}
          {unidentified.length > 0 && (
            <>
              <h3 className={run.h3}>{t("inventory.passes.unidentifiedTitle")}</h3>
              <p className={run.note}>{t("inventory.passes.unidentifiedIntro")}</p>
              <div className={styles.tableScroll}>
                <table className={styles.table} data-testid="unidentified-stock-table">
                  <thead>
                    <tr>
                      <SortableTh label={t("inventory.passes.col.address")} sortKey="address" sort={unidentifiedSort.sort} onToggle={unidentifiedSort.toggle} />
                      <SortableTh label={t("inventory.passes.col.occupancy")} sortKey="occupancy" sort={unidentifiedSort.sort} onToggle={unidentifiedSort.toggle} />
                      <SortableTh label={t("inventory.passes.col.height")} sortKey="height" sort={unidentifiedSort.sort} onToggle={unidentifiedSort.toggle} />
                    </tr>
                  </thead>
                  <tbody>
                    {unidentifiedSort.rows.map((u) => (
                      <tr key={u.address}>
                        <td className={styles.mono}>{u.address}</td>
                        {/* Null = the sensor never measured this bin; the finding is
                            the unreadable label itself. "0 %" would say empty shelf. */}
                        <td className={styles.mono}>
                          {u.occupancyPct == null ? "—" : fmtPct1(u.occupancyPct)}
                        </td>
                        <td className={styles.mono}>
                          {u.heightMm == null || u.positionHeightMm == null
                            ? "—"
                            : t("inventory.passes.heightOf", { h: u.heightMm, of: u.positionHeightMm })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CollapsibleSection>
      )}

      {/* Vertical occupancy (ADR 162): the iC3 height sensor's per-bin fill. Each
          bin is a slot drawn to its clear height, filled to the measured load —
          both heights the user asked for, grouped by Nível so "especially on
          level 2" reads at a glance. Only bins the sensor fired on appear; the
          intro states the coverage so it is never mistaken for warehouse-wide. */}
      {occ.length > 0 && (
        <CollapsibleSection id="run.occupancy" title={t("inventory.occupancy.title")} data-testid="occupancy-section">
          <p className={run.note}>
            {t("inventory.occupancy.intro", { measured: occ.length, scanned: occScanned })}
            {/* ADR 300: where the kit gave a load height and no slot height, the twin
                divided by the clear height IT states. Same rule and same wording as
                /live — derive, and say which (ADR 293). */}
            {occDerived > 0 && ` ${t("inventory.occupancy.derivedNote", { count: occDerived })}`}
          </p>

          {/* Resume: average occupancy per level, as a small vertical bar chart on
              a fixed 0–100 scale. Purely a roll-up of the measured bins below —
              the columns match the "média" in each detail group's header. */}
          <div className={run.occSummary} data-testid="occupancy-summary">
            <h3 className={run.h3}>{t("inventory.occupancy.summaryTitle")}</h3>
            <div className={run.occSummaryBars} role="list">
              {occGroups.map((g) => {
                const key = String(g.level ?? "none");
                const open = occOpen.has(key);
                const levelLabel = g.level == null
                  ? t("inventory.occupancy.noLevel")
                  : t("inventory.occupancy.level", { n: g.level });
                return (
                  // ADR 360: the column IS the disclosure control for its level's
                  // address list — detail on demand instead of a wall of rows.
                  <button
                    type="button"
                    className={open ? `${run.occCol} ${run.occColOpen}` : run.occCol}
                    key={key}
                    role="listitem"
                    aria-expanded={open}
                    onClick={() => toggleOccLevel(key)}
                    aria-label={t("inventory.occupancy.summaryAria", {
                      level: levelLabel, pct: fmtPct1(g.avgPct),
                    })}
                  >
                    <div className={run.occColPct}>{fmtPct1(g.avgPct)}</div>
                    <div className={run.occColTrack}>
                      <div className={run.occColFill} style={{ height: `${clampPct(g.avgPct)}%` }} />
                    </div>
                    <div className={run.occColLabel}>{levelLabel}</div>
                    <div className={run.occColCount}>{t("inventory.occupancy.slotsCount", { count: g.count })}</div>
                    <div className={run.occColCaret} aria-hidden="true">{open ? "▴" : "▾"}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* The plain legend credits iC3 for the fraction, which is only true where iC3
              divided it. With derived bins present the legend names both provenances. */}
          <p className={run.legend}>
            {t(occDerived > 0 ? "inventory.occupancy.legendDerived" : "inventory.occupancy.legend")}
            {" "}{t("inventory.occupancy.expandHint")}
          </p>
          {(occShowAll || occGroups.some((g) => occOpen.has(String(g.level ?? "none")) && g.hidden > 0)) && (
            <div className={run.structActions}>
              <button type="button" className={run.linkBtn} onClick={() => setOccShowAll((v) => !v)}>
                {t(occShowAll ? "inventory.occupancy.showLess" : "inventory.occupancy.showAll")}
              </button>
            </div>
          )}
          {occGroups.filter((g) => occOpen.has(String(g.level ?? "none"))).map((g) => {
            return (
              <div key={g.level ?? "none"} data-testid={`occupancy-level-${g.level ?? "none"}`}>
                <h3 className={run.h3}>
                  {g.level == null
                    ? t("inventory.occupancy.noLevel")
                    : t("inventory.occupancy.level", { n: g.level })}{" "}
                  <span className={run.levelSummary}>
                    {t("inventory.occupancy.levelSummary", { count: g.count, avg: fmtPct1(g.avgPct) })}
                  </span>
                </h3>
                <div className={run.occChart}>
                  {g.rows.map((r) => {
                    const binPct = r.binHeightMm ? (r.binHeightMm / occMaxBin) * 100 : 100;
                    return (
                      <div className={run.occRow} key={r.address}>
                        <div className={`${run.skuLabel} ${styles.mono}`}>{r.address}</div>
                        <div className={run.occTrackWrap}>
                          <div className={run.occSlot} style={{ width: `${binPct}%` }}>
                            <div className={run.occFill} style={{ width: `${clampPct(r.occupancyPct)}%` }} />
                          </div>
                        </div>
                        <div className={run.occValue}>
                          {/* ≈ on a derived bin, the same one glyph the /live KPI badge
                              uses for the same distinction (ADR 293). */}
                          <div className={styles.mono}>{r.derived ? "≈ " : ""}{fmtPct1(r.occupancyPct)}</div>
                          <div className={`${run.occHeights} ${styles.mono}`}>
                            {r.loadHeightMm == null || r.binHeightMm == null
                              ? t("inventory.occupancy.heightNa")
                              : t(r.derived ? "inventory.occupancy.heightsDerived"
                                            : "inventory.occupancy.heights", {
                                  load: fmtM(r.loadHeightMm),
                                  bin: fmtM(r.binHeightMm),
                                })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {g.hidden > 0 && (
                  <p className={run.note} role="status">
                    {t("inventory.occupancy.capped", { shown: g.rows.length, total: g.count })}
                  </p>
                )}
              </div>
            );
          })}
        </CollapsibleSection>
      )}

      {warehouseId && (
        <StructureOccupancySection siteId={siteId} warehouseId={warehouseId} runId={runId} />
      )}

      {/* ADR 195: the per-position drill-down of the structure rollup above —
          the design's coverage-grid report. Shares divRows with the WMS section
          below so both paint from one divergence fetch. */}
      {warehouseId && (
        <CoverageGridSection siteId={siteId} warehouseId={warehouseId} runId={runId} divRows={divRows} />
      )}

      {warehouseId && (
        <SlottingInsightsSection siteId={siteId} warehouseId={warehouseId} runId={runId} />
      )}

      {changeRows.length > 0 && (
        <CollapsibleSection id="run.changes" title={t("inventory.changes.title")} defaultOpen={false}>
          <div className={styles.tableScroll}>
            <table className={styles.table} data-testid="changes-table">
              <thead>
                <tr>
                  <SortableTh label={t("inventory.changes.col.address")} sortKey="address" sort={changeSort.sort} onToggle={changeSort.toggle} />
                  <SortableTh label={t("inventory.changes.col.when")} sortKey="when" sort={changeSort.sort} onToggle={changeSort.toggle} />
                  <SortableTh label={t("inventory.changes.col.before")} sortKey="before" sort={changeSort.sort} onToggle={changeSort.toggle} />
                  <SortableTh label={t("inventory.changes.col.after")} sortKey="after" sort={changeSort.sort} onToggle={changeSort.toggle} />
                  <SortableTh label={t("inventory.changes.col.outcome")} sortKey="outcome" sort={changeSort.sort} onToggle={changeSort.toggle} />
                </tr>
              </thead>
              <tbody>
                {changeSort.rows.map((c) => (
                  <tr key={c.key}>
                    <td className={styles.mono}>{c.address}</td>
                    <td className={styles.mono}>{c.when ? new Date(c.when).toLocaleString("pt-BR") : "—"}</td>
                    <td className={styles.mono}>{c.before}</td>
                    <td className={styles.mono}>{c.after}</td>
                    <td>{t(`inventory.changes.outcome.${c.outcome}`)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CollapsibleSection>
      )}

      <CollapsibleSection id="run.wms" title={t("inventory.wms.title")}>
        {/* Exports live in the body rather than beside the title: a button inside
            a <summary> activates the disclosure on click, and stopping that
            propagation to keep both behaviours is a trap for the next editor. */}
        {divRows.length > 0 && (
          <div className={run.sectionHead}>
            <button type="button" className={`${run.actionBtn} ${run.noPrint}`}
                    onClick={exportFlex} disabled={flexBusy}>
              {flexError ? t("inventory.report.exportFlexError") : t("inventory.report.exportFlex")}
            </button>
            <button type="button" className={`${run.actionBtn} ${run.noPrint}`} onClick={exportCsv}>
              {t("inventory.report.exportCsv")}
            </button>
          </div>
        )}
        {divQuery.isError ? (
          <p role="alert" className={styles.error}>{t("inventory.wms.error")}</p>
        ) : !divQuery.isLoading && divSummary?.asOf == null ? (
          <p className={styles.empty}>
            {t("inventory.wms.none")}{" "}
            <Link to="/inventory">{t("inventory.wms.upload")}</Link>
          </p>
        ) : (
          <>
            <div className={run.kpis}>
              {/* ADR 372 §4: two percentages side by side need to say what
                  they divide — pairs that agree over pairs compared, and WMS
                  addresses read over WMS addresses. */}
              <Kpi
                label={t("inventory.wms.agreement")}
                sub={t("inventory.wms.agreementSub")}
                value={divSummary ? fmtPct1(divSummary.agreementPct) : "—"}
                loading={divQuery.isLoading}
              />
              <Kpi
                label={t("inventory.wms.coverage")}
                sub={t("inventory.wms.coverageSub")}
                value={divSummary ? fmtPct1(divSummary.coverageVsWmsPct) : "—"}
                loading={divQuery.isLoading}
              />
              {/* ADR 374: what the comparison set aside. Addresses, not pairs —
                  that is the number a person can walk. */}
              <Kpi
                label={t("inventory.wms.notVisited")}
                sub={t("inventory.wms.notVisitedSub")}
                value={divSummary?.notVisitedAddresses ?? "—"}
                loading={divQuery.isLoading}
              />
              {/* ADR 384: what the closing snapshot explained — shown only when one is bound,
                  because "0 received" without a closing snapshot would claim a comparison
                  that never happened. */}
              {divSummary?.closingImportId && (
                <>
                  <Kpi label={t("inventory.wms.receivedInWindow")} sub={t("inventory.wms.receivedInWindowSub")}
                       value={divSummary.receivedInWindow ?? 0} loading={divQuery.isLoading} />
                  <Kpi label={t("inventory.wms.shippedInWindow")} sub={t("inventory.wms.shippedInWindowSub")}
                       value={divSummary.shippedInWindow ?? 0} loading={divQuery.isLoading} />
                </>
              )}
              <Kpi label={t("inventory.wms.matched")} value={divSummary?.matched ?? "—"} loading={divQuery.isLoading} />
              {/* "—", never 0, when the run carries no quantities: a zero here
                  would claim the quantities were compared and agreed. The sub
                  line says why the dash (the paragraph it replaced is gone). */}
              <Kpi label={t("inventory.wms.qtyMismatch")}
                   value={divSummary && qtyMeasurable ? divSummary.qtyMismatch : "—"}
                   sub={divSummary && !qtyMeasurable ? t("inventory.wms.qtyNotMeasuredSub") : null}
                   loading={divQuery.isLoading} />
              <Kpi label={t("inventory.wms.missing")} value={divSummary?.missingInIc3 ?? "—"} loading={divQuery.isLoading} />
              <Kpi label={t("inventory.wms.unexpected")} value={divSummary?.unexpectedInIc3 ?? "—"} loading={divQuery.isLoading} />
              {/* ADR 263. Reconciliation is per (address, item), and an unreadable
                  pallet has no item — so these bins land in none of the four counts
                  above, and the customer export flags them while this row would not.
                  Fed by the same list the section below renders, so the two can
                  never drift apart. */}
              <Kpi label={t("inventory.wms.unreadable")}
                   value={passesQuery.data ? unidentified.length : "—"}
                   sub={unidentified.length > 0 ? t("inventory.wms.unreadableSub") : null}
                   loading={passesQuery.isLoading} />
            </div>

            {/* ADR 383: which snapshots this comparison reads, and the picker to change them.
                Choosing pins the run; "Automático" hands it back to the nearest-date rule. */}
            {divSummary?.asOf && (
              <div className={run.note} data-testid="wms-binding">
                <p>
                  {t("inventory.wms.reference", { date: new Date(divSummary.asOf).toLocaleDateString("pt-BR") })}
                  {" · "}
                  {data?.run.expectedPinned ? t("inventory.wms.referencePinned") : t("inventory.wms.referenceAuto")}
                  {" · "}
                  {divSummary.closingAsOf
                    ? t("inventory.wms.closing", { date: new Date(divSummary.closingAsOf).toLocaleDateString("pt-BR") })
                    : t("inventory.wms.noClosing")}
                </p>
                {snapshotsQuery.data && snapshotsQuery.data.length > 1 && (
                  <div className={`${run.sectionHead} ${run.noPrint}`}>
                    <label>
                      {t("inventory.wms.pickReference")}{" "}
                      <select
                        value={data?.run.expectedImportId ?? ""}
                        disabled={bind.isPending}
                        aria-label={t("inventory.wms.pickReference")}
                        onChange={(e) => {
                          const ref = e.target.value || null;
                          const closing = data?.run.closingImportId ?? null;
                          bind.mutate({ expectedImportId: ref, closingImportId: closing === ref ? null : closing });
                        }}
                      >
                        {snapshotsQuery.data.map((sn) => (
                          <option key={sn.importId} value={sn.importId}>
                            {new Date(sn.asOf).toLocaleDateString("pt-BR")} · {sn.fileName}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      {t("inventory.wms.pickClosing")}{" "}
                      <select
                        value={data?.run.closingImportId ?? ""}
                        disabled={bind.isPending}
                        aria-label={t("inventory.wms.pickClosing")}
                        onChange={(e) => bind.mutate({
                          expectedImportId: data?.run.expectedImportId ?? null,
                          closingImportId: e.target.value || null,
                        })}
                      >
                        <option value="">{t("inventory.wms.pickNone")}</option>
                        {snapshotsQuery.data
                          .filter((sn) => sn.importId !== data?.run.expectedImportId)
                          .map((sn) => (
                            <option key={sn.importId} value={sn.importId}>
                              {new Date(sn.asOf).toLocaleDateString("pt-BR")} · {sn.fileName}
                            </option>
                          ))}
                      </select>
                    </label>
                    {data?.run.expectedPinned && (
                      <button type="button" className={run.actionBtn} disabled={bind.isPending}
                              onClick={() => bind.mutate({ expectedImportId: null, closingImportId: null })}>
                        {t("inventory.wms.automatic")}
                      </button>
                    )}
                    {bind.isError && <span className={styles.error} role="alert">{t("inventory.wms.bindError")}</span>}
                  </div>
                )}
              </div>
            )}

            {chartTotal(compositionRow) > 0 && (
              <section className={run.chartSection} data-testid="wms-composition-chart">
                <h3 className={run.h3}>{t("inventory.charts.composition")}</h3>
                <StackedBarChart rows={[compositionRow]} normalized totalLabel={t("inventory.wms.totalPairs")} />
              </section>
            )}
            {aisleChartRows.length > 0 && (
              <section className={run.chartSection} data-testid="wms-aisle-chart">
                <h3 className={run.h3}>{t("inventory.charts.byAisle")}</h3>
                <StackedBarChart rows={aisleChartRows} totalLabel={t("inventory.wms.totalDivergences")} />
              </section>
            )}

            {/* Aging / shelf-life (ADR 194) — WMS FIFO dates against the
                snapshot's asOf. Age never comes from scan timestamps: a scan
                only says when the camera saw the bin. */}
            {agingOn && (
              <>
                <section className={run.chartSection} data-testid="wms-aging-chart">
                  <h3 className={run.h3}>{t("inventory.aging.title")}</h3>
                  <StackedBarChart rows={agingChartRows} totalLabel={t("inventory.wms.totalPairs")} />
                </section>
                <h3 className={run.h3}>{t("inventory.aging.oldestTitle")}</h3>
                <div className={styles.tableScroll}>
                  <table className={styles.table} data-testid="aging-oldest-table">
                    <thead>
                      <tr>
                        <th>{t("inventory.wms.col.address")}</th>
                        <th>{t("inventory.wms.col.product")}</th>
                        <th>{t("inventory.wms.col.expected")}</th>
                        <th>{t("inventory.aging.col.fifo")}</th>
                        <th>{t("inventory.aging.col.age")}</th>
                        <th>{t("inventory.wms.col.status")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {oldestRows.map((r) => (
                        <tr key={`${r.address}-${r.product}`}>
                          <td className={styles.mono}>{r.address}</td>
                          <td className={styles.mono}>{r.product}</td>
                          <td className={styles.mono}>{fmtQty(r.expectedQty)}</td>
                          <td className={styles.mono}>{fmtFifoDate(r.fifoDate)}</td>
                          <td className={styles.mono}>{fmtAgeDays(r.ageDays)}</td>
                          <td>
                            <span className={`${styles.pill} ${DIV_STATUS_PILL[r.status]}`}>
                              {t(DIV_STATUS_LABEL[r.status])}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* Per-aisle reconciliation (ADR 137): the WMS agreement broken down
                by aisle so ops knows WHERE the divergences concentrate. Needs the
                address master (aisle labels) — hidden when it isn't loaded. */}
            {recon.length > 0 && (
              <>
                <h3 className={run.h3}>{t("inventory.recon.title")}</h3>
                <div className={styles.tableScroll}>
                  <table className={styles.table} data-testid="recon-table">
                    <thead>
                      <tr>
                        <SortableTh label={t("inventory.recon.col.aisle")} sortKey="aisle" sort={reconSort.sort} onToggle={reconSort.toggle} />
                        <SortableTh label={t("inventory.recon.col.keys")} sortKey="keys" sort={reconSort.sort} onToggle={reconSort.toggle} />
                        <SortableTh label={t("inventory.wms.matched")} sortKey="matched" sort={reconSort.sort} onToggle={reconSort.toggle} />
                        <SortableTh label={t("inventory.wms.qtyMismatch")} sortKey="qtyMismatch" sort={reconSort.sort} onToggle={reconSort.toggle} />
                        <SortableTh label={t("inventory.wms.missing")} sortKey="missing" sort={reconSort.sort} onToggle={reconSort.toggle} />
                        <SortableTh label={t("inventory.wms.unexpected")} sortKey="unexpected" sort={reconSort.sort} onToggle={reconSort.toggle} />
                        <SortableTh label={t("inventory.recon.col.accuracy")} sortKey="accuracy" sort={reconSort.sort} onToggle={reconSort.toggle} />
                      </tr>
                    </thead>
                    <tbody>
                      {reconSort.rows.map((a) => (
                        <tr key={a.aisle}>
                          <td className={styles.mono}>
                            {a.aisle === "__none__" ? t("inventory.recon.outOfMaster") : a.aisle}
                          </td>
                          <td className={styles.mono}>{a.keys}</td>
                          <td className={styles.mono}>{a.matched}</td>
                          <td className={styles.mono}>{qtyMeasurable ? a.qtyMismatch : "—"}</td>
                          <td className={styles.mono}>{a.missing}</td>
                          <td className={styles.mono}>{a.unexpected}</td>
                          <td className={styles.mono}>{fmtPct1(a.accuracyPct)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <div className={run.divControls}>
              <label className={run.divToggle}>
                <input
                  type="checkbox"
                  checked={showMatches}
                  onChange={(e) => setShowMatches(e.target.checked)}
                />
                <span>{t("inventory.wms.showMatches", { count: divVisible.matches })}</span>
              </label>
              {divVisible.total > DIV_ROW_CAP && (
                <span className={run.note} role="status">
                  {t("inventory.wms.capped", { shown: DIV_ROW_CAP, total: divVisible.total })}
                </span>
              )}
              {/* ADR 283: the justified/pending split — the second lens; the
                  measured agreement above never moves. */}
              {divQuery.data && divQuery.data.summary.justified > 0 && (
                <span className={run.note} role="status">
                  {t("inventory.review.split", {
                    justified: divQuery.data.summary.justified,
                    pending: divQuery.data.summary.qtyMismatch + divQuery.data.summary.missingInIc3
                      + divQuery.data.summary.unexpectedInIc3 - divQuery.data.summary.justified,
                  })}
                </span>
              )}
              {/* ADR 364: the loop's state — open recounts, and the ones a re-read closed. */}
              {divQuery.data && (divQuery.data.summary.toRecount > 0 || divQuery.data.summary.recountClosed > 0) && (
                <span className={run.note} role="status" data-testid="recount-split">
                  {t("inventory.review.recountSplit", {
                    open: divQuery.data.summary.toRecount, closed: divQuery.data.summary.recountClosed,
                  })}
                </span>
              )}
              {divQuery.data && divQuery.data.summary.toRecount > 0 && (
                <>
                  <label className={run.divToggle}>
                    <input type="checkbox" checked={onlyRecount} onChange={(e) => setOnlyRecount(e.target.checked)} />
                    <span>{t("inventory.review.onlyRecount")}</span>
                  </label>
                  <button type="button" className={run.linkBtn} onClick={exportRecountCsv}>
                    {t("inventory.review.recountList")}
                  </button>
                </>
              )}
              <button
                type="button"
                className={run.linkBtn}
                onClick={() => { setJustifyTarget(null); setJustification(""); }}
              >
                {t("inventory.review.all")}
              </button>
            </div>
            {justifyTarget !== "closed" && (
              <div className={run.reviewForm}>
                <label className={run.note} htmlFor="div-justification">
                  {justifyTarget === null
                    ? t("inventory.review.promptAll")
                    : t("inventory.review.promptOne", {
                        address: justifyTarget.address, product: justifyTarget.product || "—" })}
                </label>
                <textarea
                  id="div-justification"
                  className={run.reviewText}
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  rows={2}
                />
                <fieldset className={run.dispositions}>
                  <legend className={run.note}>{t("inventory.review.dispositionLegend")}</legend>
                  {(["accepted", "recount", "adjust_wms"] as const).map((d) => (
                    <label key={d} className={run.divToggle}>
                      <input type="radio" name="disposition" value={d} checked={disposition === d}
                             onChange={() => setDisposition(d)} />
                      <span>{t(`inventory.review.disposition.${d}`)}</span>
                    </label>
                  ))}
                </fieldset>
                <div className={run.reviewActions}>
                  <button type="button" className={run.linkBtn}
                          disabled={!justification.trim() || review.isPending}
                          onClick={() => review.mutate()}>
                    {t("inventory.review.confirm")}
                  </button>
                  <button type="button" className={run.linkBtn}
                          onClick={() => setJustifyTarget("closed")}>
                    {t("common.cancel")}
                  </button>
                  {review.isError && <span className={run.note}>{t("inventory.review.error")}</span>}
                </div>
              </div>
            )}
            <div className={styles.tableScroll}>
              <table className={styles.table} data-testid="divergence-table">
                <thead>
                  <tr>
                    <SortableTh label={t("inventory.wms.col.address")} sortKey="address" sort={divSort.sort} onToggle={divSort.toggle} />
                    <SortableTh label={t("inventory.wms.col.product")} sortKey="product" sort={divSort.sort} onToggle={divSort.toggle} />
                    <SortableTh label={t("inventory.wms.col.expected")} sortKey="expected" sort={divSort.sort} onToggle={divSort.toggle} />
                    <SortableTh label={t("inventory.wms.col.counted")} sortKey="counted" sort={divSort.sort} onToggle={divSort.toggle} />
                    {agingOn && <SortableTh label={t("inventory.aging.col.age")} sortKey="age" sort={divSort.sort} onToggle={divSort.toggle} />}
                    <SortableTh label={t("inventory.wms.col.status")} sortKey="status" sort={divSort.sort} onToggle={divSort.toggle} />
                    <th aria-label={t("inventory.review.colAria")} />
                  </tr>
                </thead>
                <tbody>
                  {divQuery.isLoading
                    ? Array.from({ length: 3 }, (_, i) => (
                        <tr key={i}>
                          {[90, 90, 60, 60, 100].map((w, j) => (
                            <td key={j}><span className="skeleton" style={{ width: w, height: 14, display: "block" }} /></td>
                          ))}
                        </tr>
                      ))
                    : divVisible.rows.map((r, i) => (
                        <tr key={`${r.address}-${r.product}-${i}`}>
                          <td className={styles.mono}>{r.address}</td>
                          <td className={styles.mono}>{r.product || "—"}</td>
                          <td className={styles.mono}>{fmtQty(r.expectedQty)}</td>
                          <td className={styles.mono}>{fmtQty(r.countedQty)}</td>
                          {agingOn && <td className={styles.mono}>{fmtAgeDays(r.ageDays)}</td>}
                          <td>
                            <span className={`${styles.pill} ${DIV_STATUS_PILL[r.status]}`}>
                              {t(DIV_STATUS_LABEL[r.status])}
                            </span>
                            {r.review && (
                              <span className={`${styles.pill} ${DISPOSITION_PILL[r.review.disposition] ?? run.reviewPill}`}
                                    title={`${r.review.justification} — ${r.review.reviewedBy}`}>
                                {t(DISPOSITION_LABEL[r.review.disposition] ?? "inventory.review.justified")}
                              </span>
                            )}
                          </td>
                          <td>
                            {r.status !== "match" && (r.review
                              ? <button type="button" className={run.linkBtn}
                                        onClick={() => unreview.mutate({ address: r.address, product: r.product })}>
                                  {t("inventory.review.undo")}
                                </button>
                              : <button type="button" className={run.linkBtn}
                                        onClick={() => { setJustifyTarget({ address: r.address, product: r.product }); setJustification(""); }}>
                                  {t("inventory.review.one")}
                                </button>)}
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CollapsibleSection>

      {/* Print-only 3D rack snapshot (ADR 163): generated to PNGs on load and
          shown only on paper, so the model lands in the PDF without adding a
          heavy canvas to the on-screen report. */}
      {rackImages.length > 0 && (
        <section className={run.rackPrint} data-testid="rack-print">
          <h2 className={run.h2}>{t("inventory.rack.title")}</h2>
          <p className={run.note}>{t("inventory.rack.intro")}</p>
          {rackImages.map((src, i) => (
            <img key={i} className={run.rackImg} src={src} alt={t("inventory.rack.alt")} />
          ))}
        </section>
      )}
    </div>
  );
}
